import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@cqt/brand";
import { AuthProvider, ToastProvider } from "@cqt/platform";
import "./index.css";
import { App } from "./App";
import { Signup } from "./Signup";

/**
 * Provider order is load-bearing:
 *   ThemeProvider  outermost, so the login splash and every error screen are
 *                  already themed — a white flash before sign-in is the most
 *                  visible bug a dark-mode user can have.
 *   ToastProvider  above AuthProvider so a boot failure can still surface.
 *   AuthProvider   blocks rendering until config + Keycloak + /me all succeed.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // The API returns 403 for "you are not allowed" — retrying that is pure
      // noise, and retrying a 401 races the token refresh.
      retry: (count, error) => {
        const status = (error as { status?: number })?.status;
        if (status && status >= 400 && status < 500) return false;
        return count < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * /signup is the ONE route that must render without authentication — the person
 * following an invite link does not have an account yet, and AuthProvider's
 * `onLoad: "login-required"` would bounce them straight to a Keycloak login they
 * cannot pass. So the branch happens here, above AuthProvider, rather than inside
 * a router below it.
 *
 * ThemeProvider still wraps both, so the signup page is themed like everything
 * else and a dark-mode user's first impression is not a white flash.
 */
const isSignup = window.location.pathname.replace(/\/+$/, "") === "/signup";

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider>
      {isSignup ? (
        <Signup />
      ) : (
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <AuthProvider>
              <App />
            </AuthProvider>
          </ToastProvider>
        </QueryClientProvider>
      )}
    </ThemeProvider>
  </StrictMode>,
);
